import type { ReactNode } from 'react';
import { Alert, Box, Breadcrumbs, Link, Paper, Skeleton, Stack, Typography, type SxProps, type Theme } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InboxIcon from '@mui/icons-material/InboxOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { errorMessage } from '../lib/api';
import { tokens } from '../app/theme';

/** TailAdmin page title row: title on the left, breadcrumb trail and actions on the right. */
export function PageHeader({
  title,
  subtitle,
  actions,
  crumbs,
  eyebrow,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  crumbs?: Array<{ label: string; to?: string }>;
  /** Small label shown above the title. */
  eyebrow?: ReactNode;
}) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2} sx={{ mb: 3 }}>
      <Box sx={{ minWidth: 0 }}>
        {eyebrow && (
          <Typography component="div" fontSize={14} fontWeight={500} color={tokens.brandText} sx={{ mb: 0.25 }}>
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h4" color="text.primary">
          {title}
        </Typography>
        {subtitle && (
          <Typography component="div" color="text.secondary" sx={{ mt: 0.5, fontSize: 14 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {(crumbs || actions) && (
        <Stack alignItems={{ sm: 'flex-end' }} gap={1.25} sx={{ flexShrink: 0 }}>
          {crumbs && (
            <Breadcrumbs separator={<ChevronRightIcon sx={{ fontSize: 16 }} />} aria-label="breadcrumb">
              {crumbs.map((c) =>
                c.to ? (
                  <Link key={c.label} component={RouterLink} to={c.to} underline="none" color="text.secondary" fontSize={14} sx={{ '&:hover': { color: tokens.brandText } }}>
                    {c.label}
                  </Link>
                ) : (
                  <Typography key={c.label} color="text.primary" fontSize={14}>
                    {c.label}
                  </Typography>
                ),
              )}
            </Breadcrumbs>
          )}
          {actions && (
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
              {actions}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/** TailAdmin card: 16px radius, hairline border, title row on top. */
export function SectionCard({
  title,
  subtitle,
  action,
  children,
  flush,
  sx,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  /** Remove body padding so tables run edge to edge. */
  flush?: boolean;
  sx?: SxProps<Theme>;
}) {
  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', ...sx }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1.5}
        sx={{ px: { xs: 2.5, md: 3 }, pt: { xs: 2.5, md: 3 }, pb: flush ? 2.5 : 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" color="text.primary" noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography fontSize={14} color="text.secondary" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      <Box
        sx={{
          flex: 1,
          px: flush ? 0 : { xs: 2.5, md: 3 },
          pb: flush ? 0 : { xs: 2.5, md: 3 },
          borderTop: flush ? `1px solid ${tokens.lineSoft}` : undefined,
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <Stack gap={3} role="status" aria-label={label}>
      <Stack direction="row" gap={3}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={160} sx={{ flex: 1, borderRadius: 4 }} />
        ))}
      </Stack>
      <Skeleton variant="rounded" height={300} sx={{ borderRadius: 4 }} />
    </Stack>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  return (
    <Alert severity="error" sx={{ my: 2 }}>
      {errorMessage(error)}
    </Alert>
  );
}

export function Empty({ title, children, icon }: { title: string; children?: ReactNode; icon?: ReactNode }) {
  return (
    <Stack alignItems="center" sx={{ py: 6, px: 2, textAlign: 'center' }} gap={1}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 3,
          display: 'grid',
          placeItems: 'center',
          bgcolor: tokens.iconBox,
          color: tokens.muted,
          mb: 1,
          '& svg': { fontSize: 26 },
        }}
      >
        {icon ?? <InboxIcon />}
      </Box>
      <Typography fontWeight={500} color="text.primary">
        {title}
      </Typography>
      {children && (
        <Typography color="text.secondary" fontSize={14} sx={{ maxWidth: 420 }}>
          {children}
        </Typography>
      )}
    </Stack>
  );
}

/** Renders loading / error states for a query, then the children. */
export function QueryState({ query, children }: { query: { isLoading: boolean; error: unknown }; children: ReactNode }) {
  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorState error={query.error} />;
  return <>{children}</>;
}
